import {useCallback, useEffect, useRef, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {Document, Page, pdfjs} from 'react-pdf'
import DraggableComponent from 'react-draggable'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {UPDATE_PDF_DOCUMENT_TEMPLATE, UPDATE_PDF_DOCUMENT_TEMPLATE_STR} from '../../../gql/mutations/pdfEnvelopesMutations'
import {NodePdfDocumentTemplate, PdfFieldDefinition, PdfFieldType, PdfSignerRole} from '../../../types/pdfEnvelopesApi'
import {parseId} from '../../../helpers'
import {STORAGE_NAMES, BACKEND_URL} from '../../../helpers/consts'
import {useUploadFile} from '../../../hooks/useUploadFile'

const Draggable = DraggableComponent as any

// pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

interface Props {
  template: NodePdfDocumentTemplate
  isOpen: boolean
  onClose: () => void
  refetch: () => void
}

const FIELD_COLORS = [
  '#2980b9', '#27ae60', '#e74c3c', '#f39c12', '#8e44ad', '#16a085',
]

const AGENT_ROLE = '__agent__'
const AGENT_COLOR = '#7f8c8d'

const FIELD_DEFAULTS: Record<PdfFieldType, {width: number; height: number; label: string}> = {
  signature:          {width: 0.18, height: 0.05,  label: 'Signature'},
  initials:           {width: 0.08, height: 0.04,  label: 'Initials'},
  full_name:          {width: 0.20, height: 0.03,  label: 'Full name'},
  first_name:         {width: 0.14, height: 0.03,  label: 'First name'},
  last_name:          {width: 0.14, height: 0.03,  label: 'Last name'},
  email:              {width: 0.20, height: 0.03,  label: 'Email'},
  phone:              {width: 0.14, height: 0.03,  label: 'Phone'},
  id_number:          {width: 0.16, height: 0.03,  label: 'ID Number'},
  date_of_birth:      {width: 0.14, height: 0.03,  label: 'Date of birth'},
  date_signed:        {width: 0.12, height: 0.03,  label: 'Date signed'},
  text:               {width: 0.18, height: 0.03,  label: 'Text'},
  checkbox:           {width: 0.02, height: 0.025, label: 'Checkbox'},
  agent_name:         {width: 0.22, height: 0.03,  label: 'Agent name'},
  agent_npn:          {width: 0.14, height: 0.03,  label: 'Agent NPN'},
  agent_phone:        {width: 0.14, height: 0.03,  label: 'Agent phone'},
  agent_email:        {width: 0.22, height: 0.03,  label: 'Agent email'},
  agent_organization: {width: 0.24, height: 0.03,  label: 'Agent organization'},
}

const FIELD_TYPES: {type: PdfFieldType; icon: string}[] = [
  {type: 'signature',   icon: '✍'},
  {type: 'initials',    icon: 'AB'},
  {type: 'full_name',   icon: '👤'},
  {type: 'first_name',  icon: '👤'},
  {type: 'last_name',   icon: '👤'},
  {type: 'email',       icon: '@'},
  {type: 'phone',       icon: '📞'},
  {type: 'id_number',   icon: '#'},
  {type: 'date_of_birth', icon: '🎂'},
  {type: 'date_signed', icon: '📅'},
  {type: 'text',        icon: 'T'},
  {type: 'checkbox',    icon: '☑'},
]

const AGENT_FIELD_TYPES: {type: PdfFieldType; icon: string}[] = [
  {type: 'agent_name',         icon: '👤'},
  {type: 'agent_npn',          icon: '#'},
  {type: 'agent_phone',        icon: '📞'},
  {type: 'agent_email',        icon: '@'},
  {type: 'agent_organization', icon: '🏢'},
]

function generateId() {
  return Math.random().toString(36).slice(2, 10)
}

function parseJsonArray(raw: any): any[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) } catch { return [] }
  }
  return []
}

interface ResizeState {
  id: string
  startX: number
  startY: number
  startW: number
  startH: number
}

export const ModalPdfFieldEditor = ({template, isOpen, onClose, refetch}: Props) => {
  const [fields, setFields] = useState<PdfFieldDefinition[]>(
    parseJsonArray(template.fieldDefinitions)
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState<{w: number; h: number} | null>(null)
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null)
  const [pendingPdfFile, setPendingPdfFile] = useState<File | null>(null)
  const [activePdfUrl, setActivePdfUrl] = useState<string | null>(null)
  const [resizing, setResizing] = useState<ResizeState | null>(null)
  const [zoom, setZoom] = useState(1.0)

  // Modal xl (~1140px) - left sidebar (160) - right sidebar (270) - padding (32) ≈ 678px available
  const BASE_PDF_WIDTH = 640
  const pdfRenderWidth = Math.round(BASE_PDF_WIDTH * zoom)

  const changeZoom = (delta: number) => {
    setZoom((z) => {
      const next = Math.round((z + delta) * 100) / 100
      return Math.max(0.5, Math.min(2.0, next))
    })
    setPageSize(null)
  }

  // Pre-fetch PDF through backend proxy to avoid S3 CORS issues
  useEffect(() => {
    if (!template.pdfFileUrl || !isOpen) return
    let blobUrl: string | null = null
    const proxyUrl = `${BACKEND_URL}/pdf-envelopes/templates/${parseId(template.id)}/pdf/`
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    fetch(proxyUrl, {headers: token ? {Authorization: `Bearer ${token}`} : {}})
      .then((r) => r.blob())
      .then((blob) => {
        blobUrl = URL.createObjectURL(blob)
        setActivePdfUrl(blobUrl)
      })
      .catch(() => setActivePdfUrl(null))
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id, isOpen])
  const overlayRef = useRef<HTMLDivElement>(null)
  const pdfInputRef = useRef<HTMLInputElement>(null)

  const signerRoles: PdfSignerRole[] = parseJsonArray(template.signerRoles) as PdfSignerRole[]
  const roleColor = (role: string) => {
    if (role === AGENT_ROLE) return AGENT_COLOR
    const idx = signerRoles.findIndex((r) => r.role === role)
    return FIELD_COLORS[idx % FIELD_COLORS.length] ?? '#2980b9'
  }
  const roleLabel = (role: string) => {
    if (role === AGENT_ROLE) return 'Agent'
    return signerRoles.find((r) => r.role === role)?.label ?? role
  }

  const [updateTemplate, {loading: savingMutation}] = useMutation(UPDATE_PDF_DOCUMENT_TEMPLATE)
  const [uploadAndUpdate, {loading: savingUpload}] = useUploadFile<any>()
  const saving = savingMutation || savingUpload

  const handlePdfFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > 20 * 1024 * 1024) {
      toast.warning('File is too large (max 20MB)')
      return
    }
    setPendingPdfFile(f)
    setActivePdfUrl(URL.createObjectURL(f))
    setPageSize(null)
    setCurrentPage(1)
  }

  const handleSave = async () => {
    const numericId = parseId(template.id)
    const fieldDefs = JSON.stringify(fields)
    try {
      if (pendingPdfFile) {
        const formdata = new FormData()
        formdata.append('0', pendingPdfFile, pendingPdfFile.name)
        formdata.append('map', '{"0": ["variables.pdfFile"]}')
        formdata.append('operations', JSON.stringify({
          query: UPDATE_PDF_DOCUMENT_TEMPLATE_STR,
          variables: {id: numericId, pdfFile: null, fieldDefinitions: fieldDefs},
        }))
        const result = await uploadAndUpdate(formdata)
        const res = result?.pdfTemplateUpdate
        if (res?.ok) {
          toast.success('Changes saved successfully')
          setPendingPdfFile(null)
          refetch()
          // Re-fetch via proxy so the new PDF shows without CORS issues
          const proxyUrl = `${BACKEND_URL}/pdf-envelopes/templates/${numericId}/pdf/`
          const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
          fetch(proxyUrl, {headers: token ? {Authorization: `Bearer ${token}`} : {}})
            .then((r) => r.blob())
            .then((blob) => setActivePdfUrl(URL.createObjectURL(blob)))
            .catch(() => {})
        } else {
          toast.error(res?.errors?.[0]?.message || 'Error saving')
        }
      } else {
        const {data} = await updateTemplate({
          variables: {id: numericId, fieldDefinitions: fieldDefs},
        })
        if (data?.pdfTemplateUpdate?.ok) {
          toast.success('Fields saved successfully')
          refetch()
        } else {
          toast.error(data?.pdfTemplateUpdate?.errors?.[0]?.message || 'Error saving')
        }
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const addField = (type: PdfFieldType) => {
    if (!pageSize) return
    const defaults = FIELD_DEFAULTS[type]
    const isAgentField = type.startsWith('agent_')
    const defaultRole = isAgentField ? AGENT_ROLE : (signerRoles[0]?.role ?? 'client')
    const newField: PdfFieldDefinition = {
      id: generateId(),
      type,
      page: currentPage,
      x: 0.35,
      y: 0.45,
      width: defaults.width,
      height: defaults.height,
      signerRole: defaultRole,
      required: type === 'signature' || type === 'initials',
      label: defaults.label,
    }
    setFields((p) => [...p, newField])
    setActiveFieldId(newField.id)
  }

  const removeField = (id: string) => {
    setFields((p) => p.filter((f) => f.id !== id))
    if (activeFieldId === id) setActiveFieldId(null)
  }

  const updateField = (id: string, updates: Partial<PdfFieldDefinition>) => {
    setFields((p) => p.map((f) => (f.id === id ? {...f, ...updates} : f)))
  }

  const onDragStop = useCallback(
    (id: string, e: any, data: {x: number; y: number}) => {
      if (!pageSize || !overlayRef.current) return
      const rect = overlayRef.current.getBoundingClientRect()
      const newX = data.x / rect.width
      const newY = data.y / rect.height
      updateField(id, {
        x: Math.max(0, Math.min(0.95, newX)),
        y: Math.max(0, Math.min(0.95, newY)),
      })
    },
    [pageSize]
  )

  const handleResizeMouseDown = (e: React.MouseEvent, field: PdfFieldDefinition) => {
    e.stopPropagation()
    e.preventDefault()
    setResizing({
      id: field.id,
      startX: e.clientX,
      startY: e.clientY,
      startW: field.width,
      startH: field.height,
    })
    setActiveFieldId(field.id)
  }

  useEffect(() => {
    if (!resizing || !pageSize) return
    const onMove = (e: MouseEvent) => {
      const dx = (e.clientX - resizing.startX) / pageSize.w
      const dy = (e.clientY - resizing.startY) / pageSize.h
      updateField(resizing.id, {
        width: Math.max(0.02, Math.min(0.98, resizing.startW + dx)),
        height: Math.max(0.01, Math.min(0.98, resizing.startH + dy)),
      })
    }
    const onUp = () => setResizing(null)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [resizing, pageSize])

  const activeField = fields.find((f) => f.id === activeFieldId)
  const currentPageFields = fields.filter((f) => f.page === currentPage)

  return (
    <Modal show={isOpen} onHide={onClose} size='xl' backdrop='static' keyboard={false}
      dialogClassName='modal-fullscreen-lg-down'>
      <Modal.Header closeButton>
        <Modal.Title>Field Editor — {template.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{padding: 0, display: 'flex', height: 'calc(100vh - 160px)', overflow: 'hidden'}}>

        {/* Left sidebar — field palette */}
        <div style={{
          width: 160, flexShrink: 0, borderRight: '1px solid #ddd',
          padding: 12, overflowY: 'auto', background: '#f8f9fa',
        }}>
          <p className='fw-bold mb-2' style={{fontSize: 12}}>Signer fields</p>
          {FIELD_TYPES.map(({type, icon}) => (
            <Button
              key={type}
              variant='outline-secondary'
              size='sm'
              className='w-100 mb-1 text-start'
              style={{fontSize: 11}}
              onClick={() => addField(type)}
            >
              <span className='me-1'>{icon}</span> {FIELD_DEFAULTS[type].label}
            </Button>
          ))}

          <hr />
          <p className='fw-bold mb-2' style={{fontSize: 12, color: AGENT_COLOR}}>Agent fields</p>
          {AGENT_FIELD_TYPES.map(({type, icon}) => (
            <Button
              key={type}
              variant='outline-secondary'
              size='sm'
              className='w-100 mb-1 text-start'
              style={{fontSize: 11, borderColor: AGENT_COLOR, color: AGENT_COLOR}}
              onClick={() => addField(type)}
            >
              <span className='me-1'>{icon}</span> {FIELD_DEFAULTS[type].label}
            </Button>
          ))}

          <hr />
          <p className='fw-bold mb-1' style={{fontSize: 12}}>PDF</p>
          <Button
            variant='outline-secondary'
            size='sm'
            className='w-100 mb-1'
            style={{fontSize: 11}}
            onClick={() => pdfInputRef.current?.click()}
          >
            <i className='bi bi-upload me-1' />
            {activePdfUrl ? 'Replace PDF' : 'Upload PDF'}
          </Button>
          {pendingPdfFile && (
            <div style={{fontSize: 10, color: '#27ae60', wordBreak: 'break-all'}}>
              {pendingPdfFile.name}
            </div>
          )}
          <input
            ref={pdfInputRef}
            type='file'
            accept='.pdf'
            style={{display: 'none'}}
            onChange={handlePdfFileChange}
          />

          <hr />
          <p className='fw-bold mb-1' style={{fontSize: 12}}>Roles</p>
          {signerRoles.map((r, i) => (
            <div key={r.role} className='d-flex align-items-center gap-1 mb-1'>
              <div style={{
                width: 10, height: 10, borderRadius: 2,
                background: FIELD_COLORS[i % FIELD_COLORS.length], flexShrink: 0,
              }} />
              <span style={{fontSize: 11}}>{r.label}</span>
            </div>
          ))}

          <hr />
          <p className='fw-bold mb-1' style={{fontSize: 12}}>Fields ({fields.length})</p>
          {fields.map((f) => (
            <div
              key={f.id}
              className='d-flex align-items-center gap-1 mb-1 p-1 rounded'
              style={{
                fontSize: 10, cursor: 'pointer',
                background: f.id === activeFieldId ? '#e8f0fe' : 'transparent',
                border: f.id === activeFieldId ? '1px solid #2980b9' : '1px solid transparent',
              }}
              onClick={() => {
                setActiveFieldId(f.id)
                if (f.page !== currentPage) setCurrentPage(f.page)
              }}
            >
              <div style={{
                width: 8, height: 8, borderRadius: 1, flexShrink: 0,
                background: roleColor(f.signerRole),
              }} />
              <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                p{f.page} {f.label}
              </span>
            </div>
          ))}
        </div>

        {/* Center — PDF canvas with overlay */}
        <div style={{flex: 1, overflowY: 'auto', padding: 16, background: '#e9ecef'}}>
          <div className='d-flex align-items-center justify-content-center gap-2 mb-2 flex-wrap'>
            <Button size='sm' variant='secondary' disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}>
              &#8592;
            </Button>
            <span style={{fontSize: 13}}>Page {currentPage} / {totalPages}</span>
            <Button size='sm' variant='secondary' disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}>
              &#8594;
            </Button>
            <div style={{width: 1, height: 20, background: '#ccc', margin: '0 4px'}} />
            <Button size='sm' variant='outline-secondary' disabled={zoom <= 0.5}
              onClick={() => changeZoom(-0.25)}
              title='Zoom out'>
              <i className='bi bi-zoom-out' />
            </Button>
            <span style={{fontSize: 12, minWidth: 38, textAlign: 'center'}}>
              {Math.round(zoom * 100)}%
            </span>
            <Button size='sm' variant='outline-secondary' disabled={zoom >= 2.0}
              onClick={() => changeZoom(0.25)}
              title='Zoom in'>
              <i className='bi bi-zoom-in' />
            </Button>
            <Button size='sm' variant='outline-secondary' disabled={zoom === 1.0}
              onClick={() => { setZoom(1.0); setPageSize(null) }}
              title='Reset zoom'>
              <i className='bi bi-aspect-ratio' />
            </Button>
          </div>

          {!activePdfUrl && (
            <div className='text-center py-5 text-muted'>
              No PDF loaded. Use "Upload PDF" in the left panel.
            </div>
          )}

          {activePdfUrl && (
            <div style={{position: 'relative', display: 'inline-block', width: '100%'}}>
              <Document
                file={activePdfUrl}
                onLoadSuccess={({numPages}) => setTotalPages(numPages)}
                loading={<div className='text-center py-4'><Spinner animation='border' /></div>}
              >
                <Page
                  pageNumber={currentPage}
                  width={pdfRenderWidth}
                  onRenderSuccess={(page) => {
                    setPageSize({w: page.width, h: page.height})
                  }}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </Document>

              {/* Overlay for field boxes */}
              {pageSize && (
                <div
                  ref={overlayRef}
                  style={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width: pageSize.w,
                    height: pageSize.h,
                    pointerEvents: 'none',
                  }}
                >
                  {currentPageFields.map((field) => {
                    const px = field.x * pageSize.w
                    const py = field.y * pageSize.h
                    const fw = field.width * pageSize.w
                    const fh = field.height * pageSize.h
                    const color = roleColor(field.signerRole)
                    const isActive = field.id === activeFieldId

                    return (
                      <Draggable
                        key={field.id}
                        defaultPosition={{x: px, y: py}}
                        position={{x: px, y: py}}
                        bounds='parent'
                        cancel='.resize-handle'
                        onStop={(e, data) => onDragStop(field.id, e, data)}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            width: fw,
                            height: fh,
                            border: `2px ${isActive ? 'solid' : 'dashed'} ${color}`,
                            background: `${color}18`,
                            cursor: 'move',
                            pointerEvents: 'all',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: Math.max(8, fh * 0.4),
                            color: color,
                            fontWeight: 'bold',
                            userSelect: 'none',
                            boxSizing: 'border-box',
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            setActiveFieldId(field.id)
                          }}
                          title={`${field.label} (${roleLabel(field.signerRole)})`}
                        >
                          {FIELD_TYPES.find((t) => t.type === field.type)?.icon} {field.label}
                          {/* Resize handle — bottom-right corner */}
                          <div
                            className='resize-handle'
                            onMouseDown={(e) => handleResizeMouseDown(e, field)}
                            style={{
                              position: 'absolute',
                              right: 0,
                              bottom: 0,
                              width: 10,
                              height: 10,
                              cursor: 'nwse-resize',
                              background: color,
                              borderRadius: '2px 0 0 0',
                              opacity: isActive ? 1 : 0.5,
                            }}
                          />
                        </div>
                      </Draggable>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right sidebar — field properties */}
        <div style={{
          width: 270, flexShrink: 0, borderLeft: '1px solid #ddd',
          padding: 12, overflowY: 'auto', background: '#f8f9fa',
        }}>
          {activeField ? (
            <>
              <p className='fw-bold mb-2' style={{fontSize: 12}}>Field properties</p>
              <Form.Group className='mb-2'>
                <Form.Label style={{fontSize: 11}}>Label</Form.Label>
                <Form.Control
                  size='sm'
                  value={activeField.label ?? ''}
                  onChange={(e) => updateField(activeField.id, {label: e.target.value})}
                />
              </Form.Group>
              <Form.Group className='mb-2'>
                <Form.Label style={{fontSize: 11}}>Signer</Form.Label>
                {activeField.signerRole === AGENT_ROLE ? (
                  <div style={{
                    fontSize: 12, padding: '4px 8px', background: `${AGENT_COLOR}18`,
                    border: `1px solid ${AGENT_COLOR}`, borderRadius: 4, color: AGENT_COLOR, fontWeight: 600,
                  }}>
                    Agent (auto-filled)
                  </div>
                ) : (
                  <Form.Select
                    size='sm'
                    value={activeField.signerRole}
                    onChange={(e) => updateField(activeField.id, {signerRole: e.target.value})}
                  >
                    {signerRoles.map((r) => (
                      <option key={r.role} value={r.role}>{r.label}</option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
              <Form.Group className='mb-2'>
                <Form.Label style={{fontSize: 11}}>Type</Form.Label>
                <Form.Select
                  size='sm'
                  value={activeField.type}
                  onChange={(e) => {
                    const t = e.target.value as PdfFieldType
                    const isAgent = t.startsWith('agent_')
                    updateField(activeField.id, {
                      type: t,
                      label: FIELD_DEFAULTS[t].label,
                      signerRole: isAgent ? AGENT_ROLE : (activeField.signerRole === AGENT_ROLE ? (signerRoles[0]?.role ?? 'client') : activeField.signerRole),
                    })
                  }}
                >
                  <optgroup label='Signer fields'>
                    {FIELD_TYPES.map(({type}) => (
                      <option key={type} value={type}>{FIELD_DEFAULTS[type].label}</option>
                    ))}
                  </optgroup>
                  <optgroup label='Agent fields'>
                    {AGENT_FIELD_TYPES.map(({type}) => (
                      <option key={type} value={type}>{FIELD_DEFAULTS[type].label}</option>
                    ))}
                  </optgroup>
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-2'>
                <Form.Label style={{fontSize: 11}}>Page</Form.Label>
                <Form.Control
                  size='sm'
                  type='number'
                  min={1}
                  max={totalPages}
                  value={activeField.page}
                  onChange={(e) => updateField(activeField.id, {page: parseInt(e.target.value) || 1})}
                />
              </Form.Group>
              <Form.Group className='mb-2'>
                <Form.Label style={{fontSize: 11}}>Width (%)</Form.Label>
                <Form.Control
                  size='sm'
                  type='number'
                  step={0.1}
                  min={0.5}
                  max={100}
                  value={(activeField.width * 100).toFixed(1)}
                  onChange={(e) => updateField(activeField.id, {width: parseFloat(e.target.value) / 100})}
                />
              </Form.Group>
              <Form.Group className='mb-2'>
                <Form.Label style={{fontSize: 11}}>Height (%)</Form.Label>
                <Form.Control
                  size='sm'
                  type='number'
                  step={0.1}
                  min={0.5}
                  max={100}
                  value={(activeField.height * 100).toFixed(1)}
                  onChange={(e) => updateField(activeField.id, {height: parseFloat(e.target.value) / 100})}
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Check
                  type='checkbox'
                  label='Required'
                  checked={activeField.required}
                  onChange={(e) => updateField(activeField.id, {required: e.target.checked})}
                />
              </Form.Group>

              {/* Default value — for text and signer data fields */}
              {['text', 'full_name', 'first_name', 'last_name', 'email', 'phone', 'id_number', 'date_of_birth'].includes(activeField.type) && (
                <Form.Group className='mb-2'>
                  <Form.Label style={{fontSize: 11}}>Default value</Form.Label>
                  <Form.Control
                    size='sm'
                    value={activeField.defaultValue ?? ''}
                    onChange={(e) => updateField(activeField.id, {defaultValue: e.target.value || undefined})}
                    placeholder='Auto-fill on send…'
                  />
                  <Form.Text className='text-muted' style={{fontSize: 10}}>
                    Pre-fills the field when sending
                  </Form.Text>
                </Form.Group>
              )}

              {/* Date format — only for date_signed */}
              {activeField.type === 'date_signed' && (
                <Form.Group className='mb-2'>
                  <Form.Label style={{fontSize: 11}}>Date format</Form.Label>
                  <Form.Select
                    size='sm'
                    value={activeField.dateFormat ?? 'DD/MM/YYYY'}
                    onChange={(e) => updateField(activeField.id, {dateFormat: e.target.value})}
                  >
                    <option value='DD/MM/YYYY'>DD/MM/YYYY (e.g. 13/05/2026)</option>
                    <option value='MM/DD/YYYY'>MM/DD/YYYY (e.g. 05/13/2026)</option>
                    <option value='YYYY-MM-DD'>YYYY-MM-DD (e.g. 2026-05-13)</option>
                    <option value='MMMM D, YYYY'>Month D, YYYY (e.g. May 13, 2026)</option>
                    <option value='D MMMM YYYY'>D Month YYYY (e.g. 13 May 2026)</option>
                    <option value='D MMM YY'>D Mon YY (e.g. 13 May 26)</option>
                  </Form.Select>
                </Form.Group>
              )}

              {/* Text formatting — only for text-type fields */}
              {!['signature', 'initials', 'checkbox'].includes(activeField.type) && (
                <>
                  <hr style={{margin: '8px 0'}} />
                  <p className='fw-bold mb-2' style={{fontSize: 11}}>Text formatting</p>
                  <Form.Group className='mb-2'>
                    <Form.Label style={{fontSize: 11}}>Font</Form.Label>
                    <Form.Select
                      size='sm'
                      value={activeField.fontFamily ?? 'helv'}
                      onChange={(e) => updateField(activeField.id, {fontFamily: e.target.value})}
                    >
                      <option value='helv'>Helvetica (default)</option>
                      <option value='tiro'>Times Roman</option>
                      <option value='cour'>Courier</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className='mb-2'>
                    <Form.Label style={{fontSize: 11}}>Font size (pt)</Form.Label>
                    <Form.Control
                      size='sm'
                      type='number'
                      min={6}
                      max={48}
                      step={1}
                      placeholder='Auto'
                      value={activeField.fontSize ?? ''}
                      onChange={(e) => {
                        const v = e.target.value
                        updateField(activeField.id, {fontSize: v === '' ? undefined : parseInt(v)})
                      }}
                    />
                    <Form.Text className='text-muted' style={{fontSize: 10}}>
                      Leave empty to auto-fit
                    </Form.Text>
                  </Form.Group>
                  <div className='d-flex flex-wrap gap-3 mb-3'>
                    <Form.Check
                      type='checkbox'
                      label='Bold'
                      checked={activeField.bold ?? false}
                      onChange={(e) => updateField(activeField.id, {bold: e.target.checked})}
                      style={{fontSize: 12}}
                    />
                    <Form.Check
                      type='checkbox'
                      label={<em>Italic</em>}
                      checked={activeField.italic ?? false}
                      onChange={(e) => updateField(activeField.id, {italic: e.target.checked})}
                      style={{fontSize: 12}}
                    />
                    <Form.Check
                      type='checkbox'
                      label={<span style={{textDecoration: 'underline'}}>Underline</span>}
                      checked={activeField.underline ?? false}
                      onChange={(e) => updateField(activeField.id, {underline: e.target.checked})}
                      style={{fontSize: 12}}
                    />
                  </div>
                </>
              )}

              <Button
                size='sm'
                variant='outline-danger'
                className='w-100'
                onClick={() => removeField(activeField.id)}
              >
                <i className='bi bi-trash' /> Delete field
              </Button>
            </>
          ) : (
            <div className='text-muted' style={{fontSize: 12}}>
              <p>Click a field to edit its properties, or drag from the palette to add a new one.</p>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <span className='text-muted me-auto' style={{fontSize: 12}}>
          {fields.length} field(s) defined
        </span>
        <Button variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size='sm' animation='border' /> : 'Save Fields'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
